import { DataTable } from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import type { BINARY_CALCULATOR_STRINGS } from "@/components/tools/binary-calculator";
import { BitGrid } from "@/components/tools/cs-tools/bit-grid";
import type {
  AddWorking,
  BitwiseOutcome,
  DivideWorking,
  MultiplyWorking,
  SubtractWorking,
  Working,
} from "@/lib/tool-kit/cs-tools/binary-calc";
import {
  groupNibbles,
  patternBits,
  patternHex,
} from "@/lib/tool-kit/cs-tools/bits";

type Strings = typeof BINARY_CALCULATOR_STRINGS;

function positions(count: number): string[] {
  return Array.from({ length: count }, (_, index) => String(count - 1 - index));
}

function AddView({ w, strings }: { w: AddWorking; strings: Strings }) {
  return (
    <BitGrid
      caption={strings.addTitle}
      corner={strings.rowLabelColumn}
      faint={w.leading}
      head={positions(w.a.length)}
      rows={[
        { label: strings.rowCarry, cells: [...w.carries] },
        { label: strings.rowA, cells: [...w.a] },
        { label: strings.rowB, cells: [...w.b] },
        { label: strings.rowSum, cells: [...w.result], strong: true },
      ]}
    />
  );
}

function SubtractView({
  w,
  strings,
}: {
  w: SubtractWorking;
  strings: Strings;
}) {
  return (
    <BitGrid
      caption={strings.subtractTitle}
      corner={strings.rowLabelColumn}
      faint={w.leading}
      head={positions(w.a.length)}
      rows={[
        { label: strings.rowBorrow, cells: [...w.borrows] },
        { label: strings.rowLarger, cells: [...w.a] },
        { label: strings.rowSmaller, cells: [...w.b] },
        { label: strings.rowDifference, cells: [...w.result], strong: true },
      ]}
    />
  );
}

function MultiplyView({
  w,
  strings,
}: {
  w: MultiplyWorking;
  strings: Strings;
}) {
  if (w.partials.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">{strings.multiplyByZero}</p>
    );
  }
  return (
    <DataTable
      caption={strings.multiplyTitle}
      head={[strings.colMultiplierBit, strings.colPartial, strings.colRunning]}
      rows={w.partials.map((p) => [p.shift, p.bits, p.running])}
    />
  );
}

function DivideView({ w, strings }: { w: DivideWorking; strings: Strings }) {
  return (
    <DataTable
      caption={strings.divideTitle}
      head={[
        strings.colPosition,
        strings.colBringDown,
        strings.colPartial2,
        strings.colFits,
        strings.colQuotientBit,
        strings.colRemainder,
      ]}
      rows={w.steps.map((s) => [
        s.position,
        s.bit,
        s.partial,
        s.subtracted ? strings.yes : strings.no,
        s.quotientBit,
        s.remainder,
      ])}
    />
  );
}

export function BinaryWorking({
  working,
  strings,
}: {
  working: Working;
  strings: Strings;
}) {
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      {working.kind === "add" ? (
        <AddView strings={strings} w={working} />
      ) : null}
      {working.kind === "subtract" ? (
        <SubtractView strings={strings} w={working} />
      ) : null}
      {working.kind === "multiply" ? (
        <MultiplyView strings={strings} w={working} />
      ) : null}
      {working.kind === "divide" ? (
        <DivideView strings={strings} w={working} />
      ) : null}
    </div>
  );
}

const GRID_MAX_WIDTH = 16;

type BitwiseOk = Extract<BitwiseOutcome, { ok: true }>;

function bitwiseRows(r: BitwiseOk, strings: Strings) {
  const rows: { label: string; value: bigint; strong?: boolean }[] = [
    { label: strings.rowA, value: r.a },
  ];
  if (r.b !== null) {
    rows.push({ label: strings.rowB, value: r.b });
  }
  rows.push({ label: strings.rowResult, value: r.result, strong: true });
  return rows;
}

export function BitwiseView({
  outcome,
  strings,
  count,
}: {
  outcome: BitwiseOk;
  strings: Strings;
  /** The shift distance, formatted for this locale. */
  count: string | null;
}) {
  const rows = bitwiseRows(outcome, strings);
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      {outcome.width <= GRID_MAX_WIDTH ? (
        <BitGrid
          caption={strings.bitwiseTitle}
          corner={strings.rowLabelColumn}
          head={positions(outcome.width)}
          rows={rows.map((row) => ({
            label: row.label,
            cells: [...patternBits(row.value, outcome.width)],
            strong: row.strong,
          }))}
        />
      ) : (
        <DataTable
          caption={strings.bitwiseTitle}
          head={[strings.colRow, strings.colBinary, strings.colHex]}
          rows={rows.map((row) => [
            row.label,
            groupNibbles(patternBits(row.value, outcome.width)),
            patternHex(row.value, outcome.width),
          ])}
        />
      )}
      {count !== null ? (
        <p className="text-muted-foreground text-sm">
          {strings.shiftDistanceLabel} {count}
          {outcome.shiftedOut !== null
            ? ` · ${strings.shiftedOutLabel} ${outcome.shiftedOut}`
            : ""}
        </p>
      ) : null}
    </div>
  );
}
